import _isNil from 'lodash/isNil';
import _isString from 'lodash/isString';
import _isArray from 'lodash/isArray';
import _isEmpty from 'lodash/isEmpty';
import Parameter, { BoolClause, CodingWithTerminologyId } from './Parameter';
import ParameterKeyValue from './ParameterKeyValue';
import { Code } from '../types';
import { OntologySearchCode } from '../../../OntologySearch/types';

export type CodeFilterCode = Code | OntologySearchCode;

export type ResolveTerminologyCodeId = (
  ontologySearchCode: OntologySearchCode,
) => Promise<string>;

/**
 * Determines if the value provided is a heirarchical ontology code or a standard code with no included terms beneath it
 * @param {Code} code - configured code value
 */
function isParentTerminologyCode(
  code: fhir.Coding | OntologySearchCode,
): code is OntologySearchCode {
  return (code as OntologySearchCode).codeType === 'ontology-code';
}

/**
 * Ensure code is a valid, trimmed string
 *
 * @param {string} code
 * @param {string} system
 * @returns {string}
 */
function formatCode(code: string, system: string): string {
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  if (_isNil(code) || !_isString(code)) return null;

  const trimmedCode = code.trim();

  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  if (_isEmpty(trimmedCode)) return null;

  const trimmedSystem =
    _isNil(system) || !_isString(system) ? null : system.trim();

  return _isEmpty(trimmedSystem)
    ? trimmedCode
    : `${trimmedSystem}|${trimmedCode}`;
}

/**
 * formats an array of configured codes into a comma separated string to be used as an API request param value
 * @param {Code[]=[]} configuredCodes - The codes from configuration
 */
function formatCodeArray(configuredCodes: Code[] = []): string {
  const codeArray = configuredCodes
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    .map((code = {} as Code) => formatCode(code.key, code.system))
    .filter(code => !_isNil(code));

  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  return codeArray.length > 0 ? codeArray.join(',') : null;
}

export type CodeFilterDocProp =
  | 'code'
  | 'medication'
  | 'type'
  | 'target.measure';

/**
 * Filter for fhir codes
 *
 * @export
 * @class CodeFilter
 * @extends {Parameter}
 */
export class CodeFilter extends Parameter {
  private _parameterKey: string;
  private _queryStringFormattedCodes: string;
  private _inputCodes: CodeFilterCode[];
  private _codeFilterDocProp: CodeFilterDocProp;
  private _resolveTerminologyCodeId: ResolveTerminologyCodeId;

  constructor(
    codes: CodeFilterCode[] = [],
    displayAllCodes: boolean,
    parameterKey: 'code' | 'measure',
    codeFilterDocProp: CodeFilterDocProp,
    resolveTerminologyCodeId: ResolveTerminologyCodeId,
  ) {
    super();

    this._codeFilterDocProp = codeFilterDocProp;
    this._parameterKey = parameterKey;
    this._codeFilterDocProp = codeFilterDocProp;
    this._resolveTerminologyCodeId = resolveTerminologyCodeId;

    if (displayAllCodes || !_isArray(codes)) {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      this._queryStringFormattedCodes = null;
      this._inputCodes = [];
    } else {
      this._queryStringFormattedCodes = formatCodeArray(codes);
      this._inputCodes = codes;
    }
  }

  get parameterKeyValue(): ParameterKeyValue {
    return new ParameterKeyValue(
      this._parameterKey,
      this._queryStringFormattedCodes,
    );
  }

  async getFSSClause(): Promise<BoolClause | null> {
    if (!this._inputCodes?.length) return null;
    const standardCodes = this._inputCodes.filter(
      code => !isParentTerminologyCode(code),
    );
    const parentTerminologyCodes = this._inputCodes
      .filter(code => isParentTerminologyCode(code))
      .map(_ => _ as OntologySearchCode);
    const resolvedParentTerminologyCodes = await Promise.all(
      parentTerminologyCodes.map(code => this._resolveTerminologyCodeId(code)),
    );
    const terminologyCodesWithId = parentTerminologyCodes.map((code, i) => {
      const resolvedTerminologyId = resolvedParentTerminologyCodes[i];
      // terminology code not found. shouldn't happen, but still at least search by code
      if (!resolvedTerminologyId) return code;

      return {
        ...code,
        terminologyId: resolvedTerminologyId,
      } as CodingWithTerminologyId;
    });

    const allCodes = [...standardCodes, ...terminologyCodesWithId];
    return {
      bool: {
        should: allCodes.map(_ =>
          this._getFSSCodeableConceptClause(this._codeFilterDocProp, _),
        ),
      },
    };
  }
}
